import { Avatar, Button, Modal } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconUser } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { useParentUpdate } from '../../api/hooks/Parent/useParentUpdate'
import { useParentGet } from '../../api/hooks/Parent/useParentGet'
import styles from '../../styles/manager/GuardianInfoModal.module.scss'
import ModalInput from '../common/ModalInput'
import { Icon } from '../../assets/icons'
import { formatPhoneNumber } from '../../utils/utils'

interface GuardianEditModalProps {
  opened: boolean
  onClose: () => void
  parentId: number
  onSave?: (updatedGuardian: any) => void
}

const GuardianEditModal = ({
  opened,
  onClose,
  parentId,onSave
}: GuardianEditModalProps) => {
  const [formData, setFormData] = useState<any>({
    NAME: '',
    EMAIL: '',
    PHONE: '',
    ADDR: '',
    USER_ID: '',
    PASSWORD: '',
    STATE: 'Y',
  })

  const { data, refetch } = useParentGet({ ID: parentId }, false)
  const updateParentMutation = useParentUpdate()

  useEffect(() => {
    if (opened) {
      refetch()
    }
  }, [opened, refetch])

  useEffect(() => {
    const parent = data?.data
    if (parent) {
      setFormData({
        NAME: parent.NAME || '',
        EMAIL: parent.EMAIL || '',
        PHONE: parent.PHONE || '',
        ADDR: parent.ADDR || '',
        USER_ID: parent.USER_ID || '',
        PASSWORD: parent.PASSWORD || '',
        STATE: parent.STATE || 'Y',
      })
    }
  }, [data])

  useEffect(() => {
    if (updateParentMutation.isSuccess) {
      notifications.show({
        title: 'Success',
        message: 'Guardian updated successfully!',
        color: 'green',
      })
      onClose()
      if(window.location.pathname !== '/admin/users') {
        window.location.reload();
      }
    }
    if (updateParentMutation.isError) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update guardian.',
        color: 'red',
      })
    }
  }, [updateParentMutation.isSuccess, updateParentMutation.isError])

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    updateParentMutation.mutate(
      {
        ...formData,
        ID: parentId,
      },
      {
        onSuccess: () => {
          const updatedGuardian = {
            GUARDIAN_NAME: formData.NAME,
            GUARDIAN_EMAIL: formData.EMAIL,
            PARENT_ID: parentId,
          }

          onSave?.(updatedGuardian)
          onClose()
        },
      }
    )
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      padding="xl"
      zIndex={1001}
      closeOnClickOutside={false}
      styles={{
        content: {
          borderRadius: '15px',
        },
        close: {
          top: '-20px',
          right: '10px',
        },
      }}
    >
      <p className={styles.title}>Edit Guardian</p>

      <div className={styles.container}>
        <div className={styles.guardianWrapper}>
          <Avatar size={80} color="gray">
            <IconUser size={40} />
          </Avatar>
          <div className={styles.inputWrapper}>
            <p className={styles.textLg}>Guardian name</p>
            <ModalInput
              value={formData.NAME}
              onChange={(e) => handleInputChange('NAME', e.target.value)}
              placeholder="Enter guardian name"
              required
            />
            <p className={styles.textLg}>Insert email</p>
            <ModalInput
              value={formData.EMAIL}
              onChange={(e) => handleInputChange('EMAIL', e.target.value)}
              placeholder="guardian@example.com"
              type="email"
              required
            />
          </div>
        </div>

        <div className={styles.sectionWrapper}>
          <div className={styles.sectionHeader}>
            <Icon name="info" size={22} />
            <p className={styles.textLg}>General information</p>
          </div>

          <div className={styles.formGrid}>
            <div className={styles.fieldItem}>
              <p className={styles.textSm}>ID</p>
              <ModalInput
                value={formData.USER_ID || ''}
                onChange={(e) => handleInputChange('USER_ID', e.target.value)}
                placeholder=""
                readOnly={true}
              />
            </div>

            <div className={styles.fieldItem}>
              <p className={styles.textSm}>Password</p>
              <ModalInput
                type="password"
                value={formData.PASSWORD || ''}
                onChange={(e) => handleInputChange('PASSWORD', e.target.value)}
                placeholder=""
              />
            </div>

            <div className={styles.fieldItem}>
              <p className={styles.textSm}>Phone</p>
              <ModalInput
                value={formData.PHONE}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value)
                  handleInputChange('PHONE', formatted)
                }}
                placeholder="000-0000-0000"
                required
              />
            </div>

            <div className={styles.fullWidthField}>
              <p className={styles.textSm}>Address</p>
              <ModalInput
                value={formData.ADDR || ''}
                onChange={(e) => handleInputChange('ADDR', e.target.value)}
                placeholder=""
              />
            </div>
          </div>
        </div>
        <div className={styles.statusSection}>
          <div className={styles.sectionHeader}>
            <Icon name="info" size={22} />
            <p className={styles.textLg}>Status</p>
          </div>
          <div className={styles.buttonWrapper}>
            <Button
              variant={formData.STATE === 'Y' ? 'filled' : 'outline'}
              color="#97A4B4"
              onClick={() => handleInputChange('STATE', 'Y')}
              className={styles.statusButton}
            >
              Active
            </Button>
            <Button
              variant={formData.STATE === 'N' ? 'filled' : 'outline'}
              color="#97A4B4"
              onClick={() => handleInputChange('STATE', 'N')}
              className={styles.statusButton}
            >
              Inactive
            </Button>
          </div>
        </div>
        <div className={styles.actionWrapper}>
          <Button
            variant="filled"
            onClick={handleSave}
            className={styles.saveButton}
            fullWidth
          >
            Save
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            className={styles.cancelButton}
            fullWidth
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default GuardianEditModal
